/**
 * Small date helpers for building "today → today + N" filter windows used by
 * the Home booking-queue widgets. Dates are emitted as `YYYY-MM-DD` in the
 * browser's local timezone, matching the bookings list date filters (which
 * compare against effective ETD/ETA/cutoff/stuffing dates).
 */

/** Format a `Date` as a local `YYYY-MM-DD` string (no timezone shift). */
export function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today as a local `YYYY-MM-DD` string. */
export function todayIso(): string {
  return toLocalIsoDate(new Date());
}

/** Today plus `days` (may be negative) as a local `YYYY-MM-DD` string. */
export function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toLocalIsoDate(d);
}

/**
 * Monday-based bounds of the ISO week containing `ref` (default: now), returned
 * as `[monday, sunday]` local `YYYY-MM-DD` strings.
 */
export function currentWeekBounds(ref: Date = new Date()): {
  from: string;
  to: string;
} {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const dow = d.getDay(); // 0 = Sunday .. 6 = Saturday
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: toLocalIsoDate(monday), to: toLocalIsoDate(sunday) };
}

/**
 * First/last calendar day of the month containing `ref` (default: now),
 * returned as local `YYYY-MM-DD` strings.
 */
export function monthBounds(ref: Date = new Date()): {
  from: string;
  to: string;
} {
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const last = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  return { from: toLocalIsoDate(first), to: toLocalIsoDate(last) };
}
